import { type ThemeOptions, type TypographyVariantsOptions } from '@mui/material/styles';

import { type ConfigObject } from '../_configs/types';
import getStyleQuery from '../utils/tools/getStyleQuery';

export function responsiveFontSizes({ xs, sm, md, lg }: { xs: number; sm: number; md: number; lg: number }) {
    return {
        fontSize: `${xs}px`,
        '@media (min-width:600px)': {
            fontSize: `${sm}px`,
        },
        '@media (min-width:900px)': {
            fontSize: `${md}px`,
        },
        '@media (min-width:1200px)': {
            fontSize: `${lg}px`,
        },
    };
}

export const typography = (raw: boolean, settingsFontSize: ConfigObject['style']['fontSize']): ThemeOptions['typography'] => {
    const styleH1: TypographyVariantsOptions['h1'] = {
        color: 'text.primary',
        fontWeight: 800,
        lineHeight: 80 / 64,
        margin: 0,
        ...responsiveFontSizes({ xs: settingsFontSize + 24, sm: settingsFontSize + 36, md: settingsFontSize + 42, lg: settingsFontSize + 50 }),
    };
    const styleH2: TypographyVariantsOptions['h2'] = {
        color: 'text.primary',
        fontWeight: 800,
        lineHeight: 64 / 48,
        margin: 0,
        ...responsiveFontSizes({ xs: settingsFontSize + 16, sm: settingsFontSize + 24, md: settingsFontSize + 28, lg: settingsFontSize + 32 }),
    };
    const styleH3: TypographyVariantsOptions['h3'] = {
        color: 'text.primary',
        fontWeight: 700,
        lineHeight: 1.5,
        margin: 0,
        ...responsiveFontSizes({ xs: settingsFontSize + 8, sm: settingsFontSize + 10, md: settingsFontSize + 14, lg: settingsFontSize + 18 }),
    };
    const styleH4: TypographyVariantsOptions['h4'] = {
        color: 'text.primary',
        fontWeight: 700,
        lineHeight: 1.5,
        margin: 0,
        ...responsiveFontSizes({ xs: settingsFontSize + 4, sm: settingsFontSize + 4, md: settingsFontSize + 8, lg: settingsFontSize + 8 }),
    };
    const styleH5: TypographyVariantsOptions['h5'] = {
        color: 'text.primary',
        fontWeight: 700,
        lineHeight: 1.5,
        margin: 0,
        ...responsiveFontSizes({ xs: settingsFontSize + 2, sm: settingsFontSize + 3, md: settingsFontSize + 4, lg: settingsFontSize + 4 }),
    };
    const styleH6: TypographyVariantsOptions['h6'] = {
        color: 'text.primary',
        fontWeight: 700,
        lineHeight: 28 / 18,
        margin: 0,
        ...responsiveFontSizes({ xs: settingsFontSize + 1, sm: settingsFontSize + 2, md: settingsFontSize + 2, lg: settingsFontSize + 2 }),
    };
    const styleSubtitle1: TypographyVariantsOptions['subtitle1'] = {
        fontSize: `${settingsFontSize}px`,
        fontWeight: 600,
        lineHeight: 1.5,
        margin: 0,
    };
    const styleSubtitle2: TypographyVariantsOptions['subtitle2'] = {
        fontSize: `${settingsFontSize - 2}px`,
        fontWeight: 600,
        lineHeight: 22 / 14,
        margin: 0,
    };
    const styleBody1: TypographyVariantsOptions['body1'] = {
        fontSize: `${settingsFontSize}px`,
        lineHeight: 1.5,
        margin: 0,
    };
    const styleBody2: TypographyVariantsOptions['body2'] = {
        fontSize: `${settingsFontSize - 2}px`,
        lineHeight: 22 / 14,
        margin: 0,
    };
    const styleButton: TypographyVariantsOptions['button'] = {
        fontSize: `${settingsFontSize}px`,
        fontWeight: 700,
        lineHeight: 24 / 14,
        margin: 0,
        textTransform: 'none',
    };
    const styleCaption: TypographyVariantsOptions['caption'] = {
        fontSize: `${settingsFontSize - 2}px`,
        lineHeight: 1.5,
        margin: 0,
    };
    const styleOverline: TypographyVariantsOptions['overline'] = {
        fontSize: `${settingsFontSize - 2}px`,
        fontWeight: 700,
        lineHeight: 1.5,
        margin: 0,
        textTransform: 'uppercase',
    };

    return {
        // Has to be set to prevent wrong html body font-family
        fontFamily: null,
        fontSize: settingsFontSize,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        // Has to be set to prevent wrong html body font-size
        htmlFontSize: null,
        h1: raw === true ? styleH1 : { [getStyleQuery()]: styleH1 },
        h2: raw === true ? styleH2 : { [getStyleQuery()]: styleH2 },
        h3: raw === true ? styleH3 : { [getStyleQuery()]: styleH3 },
        h4: raw === true ? styleH4 : { [getStyleQuery()]: styleH4 },
        h5: raw === true ? styleH5 : { [getStyleQuery()]: styleH5 },
        h6: raw === true ? styleH6 : { [getStyleQuery()]: styleH6 },
        subtitle1: raw === true ? styleSubtitle1 : { [getStyleQuery()]: styleSubtitle1 },
        subtitle2: raw === true ? styleSubtitle2 : { [getStyleQuery()]: styleSubtitle2 },
        body1: raw === true ? styleBody1 : { [getStyleQuery()]: styleBody1 },
        body2: raw === true ? styleBody2 : { [getStyleQuery()]: styleBody2 },
        button: raw === true ? styleButton : { [getStyleQuery()]: styleButton },
        caption: raw === true ? styleCaption : { [getStyleQuery()]: styleCaption },
        overline: raw === true ? styleOverline : { [getStyleQuery()]: styleOverline },
    };
};
